import React, { useState, useContext, useEffect } from 'react';
import { Button, Typography, Autocomplete, TextField, FormControlLabel, Checkbox, Box, Slider } from '@mui/material';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import '../App.css';

const User = () => {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [caresFor, setCaresFor] = useState([]);
  const [roles, setRoles] = useState([]);
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [useTabletAudio, setUseTabletAudio] = useState(true);
  const [useTime24, setUseTime24] = useState(false);
  const [useSendLog, setUseSendLog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [volume, setVolume] = useState(Constants.DEFAULT_VOLUME);

  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  let { id } = useParams();

  useEffect(() => {
    const onFetchSuccess = (user) => {
      setLastName(user.last);
      setFirstName(user.first);
      setEmail(user.email);
      setPhone(user.phone);
      setSelectedGroups(user._eventGroups);
      setRoles(user.roles);
      setCaresFor(user._careGiverFor);
      setUseTabletAudio(user.useTwilio);
      setUseSendLog(user.sendLog);
      setVolume(user.volume); 
      setUseTime24(user.time24);
    };

    const fetchUser = async (id) => {
      const params = {
        _id: id,
      };
      rpc({
        method: 'GetUser',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };

    const getUsers = async () => {
      const params = {
        _site: user._site._id,
      };
      rpc({
        method: 'GetUsers',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setUsers
      });
    };

    const getGroups = async () => {
      const params = {
        _site: user._site._id,
      };
      rpc({
        method: 'GetEventGroups',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setGroups
      });
    };

    getGroups();
    getUsers();
    if (id) {
      fetchUser(id);
    }
  }, [id, user]);


  const onSuccess = () => {
    navigate('/users');
  };

  const createUser = async () => {
    if (lastName === '' || firstName === '' || email === '' || 
    roles.length === 0 || password === '') {
      setErrorMessage('All fields except Groups are required. ' + 
        'CommunityMembers should belong to at least one Group.');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const userCaresFor = caresFor.map((user) => user._id);
    const params = {
      _site: user._site._id,
      _eventGroups: eventGroups,
      roles,
      last: lastName,
      first: firstName,
      password,
      email,
      phone,
      useTwilio: useTabletAudio,
      time24: useTime24,
      sendLog: useSendLog,
      volume,
      _careGiverFor: userCaresFor
    };
    rpc({
      method: 'CreateUser',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const updateUser = async () => {
    if (lastName === '' || firstName === '' || email === '' || roles.length === 0) {
      setErrorMessage('All fields except Password and Groups are required. ' + 
        'CommunityMembers should belong to at least one Group.');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const userCaresFor = caresFor.map((user) => user._id);
    const params = {
      _id: id,
      _eventGroups: eventGroups,
      roles,
      last: lastName,
      first: firstName,
      email,
      phone,
      time24: useTime24,
      useTwilio: useTabletAudio,
      sendLog: useSendLog,
      volume,
      _careGiverFor: userCaresFor
    };
    if (password !== '') {
      params.password = password;
    }
    rpc({
      method: 'UpdateUser',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const deleteUser = async () => {
    const params = {
      _id: id,
    };
    rpc({
      method: 'DeleteUser',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleGroupChange = (event, value) => {
    setSelectedGroups(value);
  };

  const handleRoleChange = (event, value) => {
    setRoles(value);
  };

  const handleCareForChange = (event, value) => {
    setCaresFor(value);
  };

  const getButtons = () => {
    if (id) {
      return (
        <>
          <div className="HorizontalContainer">
            <Button className="Button" variant="contained" color="primary" onClick={updateUser}>
            Update
            </Button>
            <Button className="Button" variant="outlined" color="error" onClick={deleteUser}>
            Delete
            </Button>
          </div>
        </>
      );
    } return (
      <>
        <Button className="Button" variant="contained" color="primary" onClick={createUser}>
        Create
        </Button>
      </>
    );
  };

  const roleList = [
    Constants.ROLE_COMMUNITY_ADMIN,
    Constants.ROLE_FAMILY_MEMBER,
    Constants.ROLE_SITE_ADMIN,
    Constants.ROLE_COMMUNITY_MEMBER,
    Constants.ROLE_PROGRAM_DIRECTOR,
    Constants.ROLE_INSTRUCTOR
  ];

  const getCommunityMembers = () => {
    return users.filter((user) => user.roles.includes(Constants.ROLE_COMMUNITY_MEMBER));
  };

  const getCareGiverAutoComplete = () => {
    if (roles.includes(Constants.ROLE_FAMILY_MEMBER)) {
      return (
        <Autocomplete className="Autocomplete"
          multiple
          value={caresFor}
          isOptionEqualToValue={(user, value) => user?._id === value?._id}
          options={getCommunityMembers()}
          onChange={handleCareForChange}
          getOptionLabel={(user) => `${user.first} ${user.last}`}
          renderInput={(params) => <TextField {...params} label="Cares For" variant="outlined" size="small"/>}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <div className="UserDetails">
      <Typography variant="h6" gutterBottom>
        User Details
      </Typography>
      <TextField className="TextField"
        label="Login"
        value={email}
        size="small"
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setEmail(email.trim())}
      />
      <TextField className="TextField"
        label="Password"
        type="password"
        value={password}
        size="small"
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => setPassword(password.trim())}
      />
      <TextField className="TextField"
        label="Phone"
        value={phone}
        size="small"
        onChange={(e) => setPhone(e.target.value)}
        onBlur={() => setPhone(phone.trim())}
      />
      <TextField className="TextField"
        label="Last Name"
        value={lastName}
        size="small"
        onChange={(e) => setLastName(e.target.value)}
      />
      <TextField className="TextField"
        label="First Name"
        value={firstName}
        size="small"
        onChange={(e) => setFirstName(e.target.value)}
      />
      <Autocomplete className="Autocomplete"
        multiple
        value={selectedGroups}
        isOptionEqualToValue={(group, value) => group?._id === value?._id}
        options={groups}
        onChange={handleGroupChange}
        getOptionLabel={(group) => group.displayName}
        renderInput={(params) => <TextField {...params} label="Groups" variant="outlined" size="small"/>}
      />
      <Autocomplete className="Autocomplete"
        multiple
        value={roles}
        options={roleList}
        onChange={handleRoleChange}
        renderInput={(params) => <TextField {...params} label="Roles" variant="outlined" size="small"/>}
      />
      <FormControlLabel
        control={<Checkbox checked={useTabletAudio} 
          onChange={(e) => setUseTabletAudio(e.target.checked)} />}
        label="Use Tablet Audio"
      />
      <FormControlLabel
        control={<Checkbox checked={useTime24} 
          onChange={(e) => setUseTime24(e.target.checked)} />}
        label="Use 24-Hour Time"
      />
      <FormControlLabel
        control={<Checkbox checked={useSendLog} 
          onChange={(e) => setUseSendLog(e.target.checked)} />}
        label="Send Log"
      />
      <Box sx={{ width: 300, px: 2, py: 1 }}>
        <Typography id="input-slider" gutterBottom>
          Tablet Volume ({volume}%)
        </Typography>
        <Slider
          value={volume}
          onChange={handleVolumeChange}
          aria-labelledby="input-slider"
          valueLabelDisplay="auto"
          step={1}
          min={0}
          max={100}
        />
      </Box>
      {getCareGiverAutoComplete()}
      {getButtons()}
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

export default User;
